/**
 * EyeProtectionToggle - UI component for eye comfort settings
 *
 * Eye Protection mode levels:
 * - Normal (0): All animations and full colors
 * - Eye Shield (1): Same colors, no animations (static previews)
 * - Eye Shield+ (2): No animations AND muted/smoothed colors
 */

import { SoundManager, SoundType } from '../audio/SoundManager';

const STORAGE_KEY = 'wooorms_eye_protection_level';

/**
 * Eye protection level enum
 */
export enum EyeProtectionLevel {
  OFF = 0,      // Normal - all animations, all colors
  MILD = 1,     // Eye Shield - no animations, normal colors
  STRONG = 2,   // Eye Shield+ - no animations, muted colors
}

/**
 * Level configuration for display
 */
const LEVEL_CONFIG = {
  [EyeProtectionLevel.OFF]: {
    name: 'Normal',
    description: 'Full experience',
    icon: '✨',
    color: '#666',
    activeColor: '#4ecdc4',
  },
  [EyeProtectionLevel.MILD]: {
    name: 'Eye Shield',
    description: 'No animations',
    icon: '👁️',
    color: '#666',
    activeColor: '#f9ed69',
  },
  [EyeProtectionLevel.STRONG]: {
    name: 'Eye Shield+',
    description: 'Calmer colors',
    icon: '🛡️',
    color: '#666',
    activeColor: '#ff6b6b',
  },
};

/**
 * Event handlers for EyeProtectionToggle
 */
export interface EyeProtectionToggleEvents {
  onEyeProtectionChange: (level: EyeProtectionLevel) => void;
}

/**
 * EyeProtectionToggle - UI for enabling eye protection mode
 */
export class EyeProtectionToggle {
  /** Container element */
  private container: HTMLDivElement;

  /** Current eye protection level */
  private level: EyeProtectionLevel;

  /** Event handlers */
  private events: EyeProtectionToggleEvents;

  /** Level button elements */
  private levelButtons: HTMLButtonElement[] = [];

  constructor(events: EyeProtectionToggleEvents) {
    this.events = events;
    this.level = this.loadFromStorage();

    // Create container
    this.container = document.createElement('div');
    this.container.style.cssText = `
      width: 100%;
      margin-top: 12px;
    `;

    this.createUI();
  }

  /**
   * Load setting from localStorage
   */
  private loadFromStorage(): EyeProtectionLevel {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        const level = parseInt(stored, 10);
        if (level >= 0 && level <= 2) {
          return level as EyeProtectionLevel;
        }
      }
      // Migration from old boolean storage
      const oldStored = localStorage.getItem('wooorms_eye_protection');
      if (oldStored === 'true') {
        return EyeProtectionLevel.STRONG;
      }
    } catch {
      // Ignore storage errors
    }
    return EyeProtectionLevel.OFF;
  }

  /**
   * Save setting to localStorage
   */
  private saveToStorage(): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(this.level));
    } catch {
      // Ignore storage errors
    }
  }

  /**
   * Create the UI
   */
  private createUI(): void {
    // Section header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      align-items: center;
      gap: 8px;
      margin-bottom: 8px;
    `;

    const icon = document.createElement('span');
    icon.textContent = '👁️';
    icon.style.fontSize = '16px';

    const title = document.createElement('span');
    title.textContent = 'EYE COMFORT';
    title.style.cssText = `
      font-size: 11px;
      font-weight: 700;
      color: #4ecdc4;
      letter-spacing: 2px;
      text-transform: uppercase;
    `;

    header.appendChild(icon);
    header.appendChild(title);
    this.container.appendChild(header);

    // Main container
    const mainContainer = document.createElement('div');
    mainContainer.style.cssText = `
      padding: 12px 16px;
      background: linear-gradient(145deg, rgba(30, 30, 45, 0.9), rgba(20, 20, 30, 0.9));
      border-radius: 10px;
      border: 1px solid rgba(78, 205, 196, 0.2);
    `;

    // Label row
    const labelRow = document.createElement('div');
    labelRow.style.cssText = `
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 12px;
    `;

    const label = document.createElement('span');
    label.textContent = 'Eye Protection Level';
    label.style.cssText = `
      font-size: 14px;
      font-weight: 600;
      color: #fff;
    `;

    labelRow.appendChild(label);
    mainContainer.appendChild(labelRow);

    // Level buttons row
    const buttonsRow = document.createElement('div');
    buttonsRow.style.cssText = `
      display: flex;
      gap: 8px;
    `;

    // Create 3 level buttons
    const levels = [EyeProtectionLevel.OFF, EyeProtectionLevel.MILD, EyeProtectionLevel.STRONG];
    levels.forEach((levelValue) => {
      const config = LEVEL_CONFIG[levelValue];
      const button = this.createLevelButton(levelValue, config);
      this.levelButtons.push(button);
      buttonsRow.appendChild(button);
    });

    mainContainer.appendChild(buttonsRow);
    this.container.appendChild(mainContainer);

    // What it does section
    const infoSection = document.createElement('div');
    infoSection.style.cssText = `
      margin-top: 8px;
      padding: 10px 12px;
      background: rgba(20, 20, 30, 0.6);
      border-radius: 8px;
      border: 1px solid rgba(78, 205, 196, 0.1);
    `;

    const infoTitle = document.createElement('div');
    infoTitle.textContent = 'Protection levels:';
    infoTitle.style.cssText = `
      font-size: 10px;
      font-weight: 600;
      color: #666;
      margin-bottom: 6px;
      text-transform: uppercase;
      letter-spacing: 1px;
    `;

    const infoList = document.createElement('ul');
    infoList.style.cssText = `
      margin: 0;
      padding-left: 16px;
      font-size: 11px;
      color: #777;
      line-height: 1.6;
    `;

    const effects = [
      { label: 'Normal', desc: 'All animations and colors enabled' },
      { label: 'Eye Shield', desc: 'Static previews, no animations' },
      { label: 'Eye Shield+', desc: 'Static + muted/softened colors' },
    ];

    effects.forEach((effect) => {
      const li = document.createElement('li');
      const strong = document.createElement('strong');
      strong.textContent = effect.label + ': ';
      strong.style.color = '#999';
      li.appendChild(strong);
      li.appendChild(document.createTextNode(effect.desc));
      infoList.appendChild(li);
    });

    infoSection.appendChild(infoTitle);
    infoSection.appendChild(infoList);
    this.container.appendChild(infoSection);
  }

  /**
   * Create a level button
   */
  private createLevelButton(
    levelValue: EyeProtectionLevel,
    config: { name: string; description: string; icon: string; color: string; activeColor: string }
  ): HTMLButtonElement {
    const isActive = this.level === levelValue;
    const button = document.createElement('button');
    button.style.cssText = `
      flex: 1;
      padding: 10px 8px;
      border-radius: 8px;
      border: 2px solid ${isActive ? config.activeColor : 'rgba(60, 60, 80, 0.8)'};
      cursor: pointer;
      transition: all 0.2s ease;
      background: ${isActive ? `rgba(${this.hexToRgb(config.activeColor)}, 0.2)` : 'rgba(30, 30, 45, 0.8)'};
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 4px;
    `;

    const iconSpan = document.createElement('span');
    iconSpan.textContent = config.icon;
    iconSpan.style.cssText = `font-size: 18px;`;

    const nameSpan = document.createElement('span');
    nameSpan.textContent = config.name;
    nameSpan.style.cssText = `
      font-size: 12px;
      font-weight: 700;
      color: ${isActive ? config.activeColor : '#888'};
    `;

    const descSpan = document.createElement('span');
    descSpan.textContent = config.description;
    descSpan.style.cssText = `
      font-size: 9px;
      color: ${isActive ? config.activeColor : '#555'};
      opacity: 0.8;
    `;

    button.appendChild(iconSpan);
    button.appendChild(nameSpan);
    button.appendChild(descSpan);

    // Hover effect
    button.addEventListener('mouseover', () => {
      if (this.level !== levelValue) {
        button.style.borderColor = 'rgba(78, 205, 196, 0.5)';
        button.style.background = 'rgba(78, 205, 196, 0.1)';
      }
      SoundManager.getInstance().play(SoundType.UI_HOVER);
    });

    button.addEventListener('mouseout', () => {
      if (this.level !== levelValue) {
        button.style.borderColor = 'rgba(60, 60, 80, 0.8)';
        button.style.background = 'rgba(30, 30, 45, 0.8)';
      }
    });

    // Click handler
    button.addEventListener('click', () => {
      this.setLevel(levelValue);
      SoundManager.getInstance().play(SoundType.UI_CLICK);
    });

    return button;
  }

  /**
   * Convert hex color to RGB string
   */
  private hexToRgb(hex: string): string {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (result && result[1] && result[2] && result[3]) {
      return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}`;
    }
    return '78, 205, 196';
  }

  /**
   * Set the eye protection level
   */
  private setLevel(newLevel: EyeProtectionLevel): void {
    if (this.level === newLevel) return;

    this.level = newLevel;
    this.updateButtonVisuals();
    this.saveToStorage();
    this.events.onEyeProtectionChange(this.level);
  }

  /**
   * Update all button visuals
   */
  private updateButtonVisuals(): void {
    const levels = [EyeProtectionLevel.OFF, EyeProtectionLevel.MILD, EyeProtectionLevel.STRONG];
    levels.forEach((levelValue, index) => {
      const button = this.levelButtons[index];
      if (!button) return;

      const config = LEVEL_CONFIG[levelValue];
      const isActive = this.level === levelValue;

      button.style.borderColor = isActive ? config.activeColor : 'rgba(60, 60, 80, 0.8)';
      button.style.background = isActive
        ? `rgba(${this.hexToRgb(config.activeColor)}, 0.2)`
        : 'rgba(30, 30, 45, 0.8)';

      const nameSpan = button.querySelector('span:first-child') as HTMLSpanElement;
      const descSpan = button.querySelector('span:last-child') as HTMLSpanElement;

      if (nameSpan) {
        nameSpan.style.color = isActive ? config.activeColor : '#888';
      }
      if (descSpan) {
        descSpan.style.color = isActive ? config.activeColor : '#555';
      }
    });
  }

  /**
   * Get the current eye protection level
   */
  getLevel(): EyeProtectionLevel {
    return this.level;
  }

  /**
   * Legacy method - returns true if any protection is enabled
   */
  isEnabled(): boolean {
    return this.level > EyeProtectionLevel.OFF;
  }

  /**
   * Get the container element
   */
  getElement(): HTMLDivElement {
    return this.container;
  }

  /**
   * Destroy the component
   */
  destroy(): void {
    this.container.remove();
  }
}

/**
 * Global eye protection settings manager
 * Can be accessed throughout the app to check eye protection level
 */
export class EyeProtectionSettings {
  private static instance: EyeProtectionSettings | null = null;
  private level: EyeProtectionLevel = EyeProtectionLevel.OFF;
  private listeners: Set<(level: EyeProtectionLevel) => void> = new Set();

  private constructor() {
    this.loadFromStorage();
  }

  static getInstance(): EyeProtectionSettings {
    if (!EyeProtectionSettings.instance) {
      EyeProtectionSettings.instance = new EyeProtectionSettings();
    }
    return EyeProtectionSettings.instance;
  }

  private loadFromStorage(): void {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored !== null) {
        const level = parseInt(stored, 10);
        if (level >= 0 && level <= 2) {
          this.level = level as EyeProtectionLevel;
          return;
        }
      }
      // Migration from old boolean storage
      const oldStored = localStorage.getItem('wooorms_eye_protection');
      if (oldStored === 'true') {
        this.level = EyeProtectionLevel.STRONG;
        return;
      }
    } catch {
      this.level = EyeProtectionLevel.OFF;
    }
  }

  /**
   * Get the current protection level
   */
  getLevel(): EyeProtectionLevel {
    return this.level;
  }

  /**
   * Check if animations are enabled (only in Normal mode)
   */
  areAnimationsEnabled(): boolean {
    return this.level === EyeProtectionLevel.OFF;
  }

  /**
   * Check if colors should be muted (only in Eye Shield+ mode)
   */
  shouldMuteColors(): boolean {
    return this.level === EyeProtectionLevel.STRONG;
  }

  /**
   * Legacy method - returns true if any protection is enabled
   */
  isEnabled(): boolean {
    return this.level > EyeProtectionLevel.OFF;
  }

  /**
   * Check if mild protection or higher is enabled
   */
  isMildOrHigher(): boolean {
    return this.level >= EyeProtectionLevel.MILD;
  }

  /**
   * Check if strong protection is enabled
   */
  isStrong(): boolean {
    return this.level === EyeProtectionLevel.STRONG;
  }

  /**
   * Set the protection level
   */
  setLevel(level: EyeProtectionLevel): void {
    this.level = level;
    try {
      localStorage.setItem(STORAGE_KEY, String(level));
    } catch {
      // Ignore
    }
    this.notifyListeners();
  }

  /**
   * Legacy method - set enabled state (maps to STRONG when true)
   */
  setEnabled(enabled: boolean): void {
    this.setLevel(enabled ? EyeProtectionLevel.STRONG : EyeProtectionLevel.OFF);
  }

  addListener(callback: (level: EyeProtectionLevel) => void): void {
    this.listeners.add(callback);
  }

  removeListener(callback: (level: EyeProtectionLevel) => void): void {
    this.listeners.delete(callback);
  }

  private notifyListeners(): void {
    this.listeners.forEach((callback) => callback(this.level));
  }
}
